import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { formatPhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import PhoneInput from '@components/fields/PhoneInput';
import Form from '@components/forms/Form';
import useMyClient from '@hooks/useMyClient';
import {
  PhoneNumberChannelEnum,
  PhoneVerificationCreateDocument,
  PhoneVerificationCreateMutation,
  PhoneVerificationCreateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  number: string;
};

type Props = {
  forConsults?: boolean;
  initialValue?: string | null;
  onCancel?: () => void;
  onSuccess: (phoneNumber?: string) => void;
};

const PhoneNumberForm = ({ forConsults = false, initialValue, onCancel, onSuccess }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createPhoneNumber] = useMutation<PhoneVerificationCreateMutation, PhoneVerificationCreateMutationVariables>(
    PhoneVerificationCreateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      number: yup.string().required(intl.formatMessage({ id: 'errors.phoneNumber.number.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: { number: initialValue ?? myClient?.consultPhoneNumber?.number ?? '' },
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const number = form.watch('number');

  const isValid = useMemo(() => {
    if (number && number !== '') {
      return isValidPhoneNumber(number);
    } else {
      return false;
    }
  }, [number]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    if (forConsults) return onSuccess(number);

    return createPhoneNumber({
      variables: {
        channel: PhoneNumberChannelEnum.sms,
        formattedNumber: formatPhoneNumber(number),
        number
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.phoneVerificationCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.phoneNumber'
      });
    });
  }, [createPhoneNumber, forConsults, form.setError, intl, number, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <PhoneInput<FormData> autofocus control={form.control} error={form.formState.errors.number} name="number" />
      <FormButtons
        cancelKey={forConsults ? undefined : 'pages.client.account.twoFactor.setUpLater'}
        hookForm={form}
        onCancel={onCancel}
        saveDisabled={!isValid}
        saveKey={forConsults ? undefined : 'pages.client.account.twoFactor.sendCode'}
      />
    </Form>
  );
};

export default PhoneNumberForm;
